import { ArrowRight, Send, ArrowUp } from 'lucide-react';
import { FaXTwitter, FaInstagram, FaLinkedin, FaTelegram } from 'react-icons/fa6';
import { useState, useEffect } from 'react';

// Components
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import ReferralSection from '../components/ReferralSection';
import ServiceBlock from '../components/ServiceBlock';

// Context
import { useLanguage } from '../context/LanguageContext';

// Data
import { servicesLeft, servicesRightTop, servicesRightBottom } from '../data/services';
import { serviceBlocksData, customImageComponents } from '../data/serviceBlocks';

const AllServicesPage = () => {
  const [showScrollTop, setShowScrollTop] = useState(false);
  const { t } = useLanguage();

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 800);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const scrollToService = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  // Render service card
  const ServiceCard = ({ service, index, accentColor = 'purple', featured = false }) => {
    const baseClasses = "hover:bg-gray-700 border px-3 md:px-4 py-3 md:py-4 rounded-lg md:rounded-xl text-left transition-all group h-auto md:h-[85px] w-full";
    const featuredClasses = "bg-gradient-to-br from-yellow-500/20 to-yellow-600/10 border-yellow-500/50 hover:border-yellow-400";
    const normalPurple = "bg-gray-800 border-gray-700 hover:border-purple-500/50";
    const normalYellow = "bg-gray-800 border-gray-700 hover:border-yellow-500/50";
    
    const cardClasses = featured 
      ? `${baseClasses} ${featuredClasses}`
      : `${baseClasses} ${accentColor === 'yellow' ? normalYellow : normalPurple}`;

    // Featured card has purple number, others follow accentColor
    const numberColor = featured ? 'text-purple-400' : (accentColor === 'yellow' ? 'text-yellow-500' : 'text-purple-400');
    const hoverColor = featured || accentColor === 'yellow' ? 'group-hover:text-yellow-400' : 'group-hover:text-purple-400';
    const displayNumber = service.num || String(index + 1).padStart(2, '0');

    return (
      <button
        onClick={() => scrollToService(service.id)}
        className={cardClasses}
        data-testid={`service-card-${service.id}`}
      >
        <div className="flex items-center gap-1 md:gap-2">
          <span className={`${numberColor} text-xs md:text-sm font-mono font-bold`}>{displayNumber}</span>
          <h3 className={`text-white font-bold text-[10px] md:text-sm ${hoverColor} transition-colors leading-tight`}>{service.name}</h3>
        </div>
        <p className="text-gray-400 text-[9px] md:text-xs mt-1 md:mt-2 ml-4 md:ml-7 leading-tight hidden md:block">
          {service.badge && <span className="text-yellow-400 font-semibold">{service.badge} → </span>}
          {service.subtitle}
        </p>
      </button>
    );
  };

  return (
    <div className="min-h-screen bg-gray-950" data-testid="all-services-page">
      <Navbar />

      {/* Floating scroll to top button */}
      {showScrollTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-8 right-8 z-50 bg-purple-600 hover:bg-purple-500 text-white p-4 rounded-full shadow-lg transition-all hover:scale-110"
          aria-label="Scroll to top"
        >
          <ArrowUp size={24} />
        </button>
      )}
      
      {/* Hero Section */}
      <section className="relative min-h-screen md:h-screen flex items-center md:items-end justify-center pt-20 md:pt-0 pb-6 md:pb-8 bg-gray-950">
        <img 
          src="https://customer-assets.emergentagent.com/job_realestate-fund-6/artifacts/qsxu12yq_1_LE_upscale_balanced_x4.webp"
          alt="Background"
          className="absolute inset-0 w-full h-full object-cover"
          loading="eager"
          fetchPriority="high"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-gray-950/80 via-gray-950/70 to-gray-950"></div>
        
        <div className="relative z-10 w-full max-w-[1400px] mx-auto px-4 text-center">
          <h1 className="text-2xl md:text-4xl lg:text-5xl font-bold text-white mb-4 md:mb-6">
            {t('services2.title')}
          </h1>
          
          <p className="text-sm md:text-lg text-gray-300 mb-2 md:mb-3 max-w-3xl mx-auto leading-relaxed italic">
            {t('services2.founderDesc')}
          </p>
          <p className="text-sm md:text-lg text-gray-300 mb-4 md:mb-6 max-w-3xl mx-auto leading-relaxed italic">
            {t('services2.networkDesc')}
          </p>
          
          {/* Current Focus */}
          <div className="bg-gradient-to-r from-yellow-500/20 via-yellow-400/10 to-yellow-500/20 border border-yellow-500/30 rounded-xl px-4 md:px-6 py-3 md:py-4 mb-4 md:mb-8 max-w-2xl mx-auto">
            <p className="text-yellow-400 font-bold text-xs md:text-sm tracking-wide mb-1">{t('services2.currentFocus')}: "AAC REAL ESTATE I"</p>
            <p className="text-white text-base md:text-lg font-semibold">15-40% annualized returns</p>
            <p className="text-gray-300 text-xs md:text-sm">{t('services2.lookingInvestors')} · <span className="text-yellow-400">{t('services2.referralAvailable')}</span></p>
          </div>
          
          {/* Social Links - compact on mobile */}
          <div className="flex flex-wrap justify-center gap-2 md:gap-3 mb-8 md:mb-12">
            <a href="https://x.com/lil_jimmo" target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-1 md:gap-2 bg-gray-800/80 hover:bg-gray-700 text-white px-3 md:px-4 py-2 md:py-3 rounded-lg transition-colors text-sm">
              <FaXTwitter size={16} /> <span className="hidden md:inline">@lil_jimmo</span>
            </a>
            <a href="https://www.instagram.com/accretion.capital/" target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-2 bg-gray-800/80 hover:bg-gray-700 text-white px-3 md:px-4 py-2 md:py-3 rounded-lg transition-colors">
              <FaInstagram size={16} />
            </a>
            <a href="https://www.linkedin.com/company/accretion-axiom-capital/" target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-2 bg-gray-800/80 hover:bg-gray-700 text-white px-3 md:px-4 py-2 md:py-3 rounded-lg transition-colors">
              <FaLinkedin size={16} />
            </a>
            <a href="https://t.me/+gbLD_8i3AbIzMTY0" target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-1 md:gap-2 bg-purple-600/80 hover:bg-purple-500 text-white px-3 md:px-4 py-2 md:py-3 rounded-lg transition-colors text-sm">
              <FaTelegram size={16} /> <span className="hidden md:inline">Deals Group</span>
            </a>
          </div>
          
          {/* Service Cards Grid */}
          <div className="bg-gray-900/90 backdrop-blur-sm rounded-2xl p-4 md:p-6 w-full">
            <p className="text-center text-[10px] md:text-xs tracking-[0.15em] md:tracking-[0.2em] text-gray-500 mb-4 md:mb-6 mt-1 md:mt-2">
              {t('services2.clickToView')}
            </p>
            
            <div className="flex flex-col lg:flex-row gap-6 justify-center items-stretch">
              {/* Left Column - Investment & Financial */}
              <div className="lg:w-[48%]">
                <p className="text-xs text-purple-400 font-medium mb-4 tracking-wider text-center lg:text-right">
                  {t('services2.investmentFinancial')}
                </p>
                <div className="grid grid-cols-2 gap-3">
                  {servicesLeft.map((service, index) => (
                    <ServiceCard 
                      key={service.id} 
                      service={service} 
                      index={index}
                      accentColor="purple"
                      featured={index === 0}
                    />
                  ))}
                </div>
              </div>
              
              {/* Vertical Line - Centered */}
              <div className="hidden lg:flex items-center">
                <div className="w-px h-full bg-gray-700"></div>
              </div>
              
              {/* Right Column - Luxury & Assets */}
              <div className="lg:w-[48%]">
                <p className="text-xs text-yellow-500 font-medium mb-4 tracking-wider text-center lg:text-left">
                  {t('services2.luxuryAssets')}
                </p>
                <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
                  {[...servicesRightTop, ...servicesRightBottom].map((service) => (
                    <ServiceCard key={service.id} service={service} accentColor="yellow" />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Referral Section */}
      <ReferralSection />
      
      {/* Service Detail Blocks - Rendered from data */}
      {serviceBlocksData.map((block) => (
        <ServiceBlock
          key={block.id}
          id={block.id}
          number={block.number}
          title={block.title}
          subtitle={block.subtitle}
          description={block.description}
          bulletPoints={block.bulletPoints}
          cta={block.cta}
          ctaInvest={block.ctaInvest}
          image={block.image}
          accentColor={block.accentColor}
          bgColor={block.bgColor}
          imageLeft={block.imageLeft}
          customImageContent={block.customImageContent ? customImageComponents[block.customImageContent] : null}
          extraContent={block.extraContent === 'tax-structures' ? (
            <div className="border-l-2 border-yellow-500 pl-4 mb-6 bg-yellow-500/5 py-2">
              <p className="text-yellow-400/90 text-sm italic">
                OnlyFans & Content Creators → Legal 0% Tax Structures
              </p>
            </div>
          ) : null}
        />
      ))}

      {/* Final CTA */}
      <section className="py-20 bg-gray-950 border-t border-gray-800">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-white mb-6">{t('services2.readyConnect')}</h2>
          <p className="text-gray-400 mb-8">
            {t('services2.contactDesc')}
          </p>
          <a 
            href="https://t.me/+gbLD_8i3AbIzMTY0" 
            target="_blank" 
            rel="noopener noreferrer"
            className="bg-purple-600 hover:bg-purple-500 text-white px-10 py-4 rounded-lg inline-flex items-center font-bold text-lg transition-all"
          >
            <Send className="mr-3" size={20} />
            {t('services2.contactTelegram')}
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AllServicesPage;
